import React, { useCallback, useEffect, useState } from "react";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import BoxHeader from "../../Components/Box/BoxHeader";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Switch,
  Box,
  Grid,
  FormLabel,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  MenuItem,
} from "@mui/material";
import { useDispatch } from "react-redux"; // Add this import
import { useNavigate } from "react-router-dom";
import SelectedUserMenu from "../../Assets/images/menu-icons/manage_accounts_selected.png";
import { AntTab, AntTabs } from "../../Components/Tab/AntTab";
import { PermissionCheck } from "../../App/PermissionCheck";
import {
  GetApiMethod,
  PostApiMethod,
  PutApiMethod,
  DeleteApiMethod
} from "../../Utils/ApiService";
import { showLoader } from "../../Store/loaderSlice";
import { RoleDto, UserDto } from "../../Dto/Generic";
import MenuGroup from "../../Components/DropDown/MenuGroup";
import { CustomModal, ModalStyle } from "../../Components/ModalStyle";
import { Form, Formik } from "formik";
import { userSchema, getUserSchema } from "./AddUserSchema";
import { DisplayErrorMessages } from "../../Utils/HelperService";
import { showToaster } from "../../Store/toastSlice";
import { CustomTablePagination } from "../../Components/Pagination/CustomTablePagination";
import TableSearch from "../../Components/Search/TableSearch";

interface UserListDto {
  records: UserDto[];
  totalCount: number;
}

// Helper to determine fields based on role (case-insensitive)
const getRoleSpecificFields = (roleName: string) => {
  const normalized = (roleName || "").toLowerCase();
  switch (normalized) {
    case "player":
      return ["height", "dateOfBirth", "position", "city", "state", "country"];
    case "scout":
    case "coach":
      return ["dateOfBirth", "club", "city", "state", "country"];
    default:
      return [];
  }
};
const UserManagement = () => {
  const [userList, setUserList] = useState<UserListDto>({
    records: [],
    totalCount: 0,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [formModal, setFormModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState("");

  const [userData, setUserData] = useState<UserDto>({
    id: "",
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    status: false,
    roles: [],
    height: "",
    dateOfBirth: "",
    position: "",
    city: "",
    state: "",
    country: "Australia",
    club: "",
    nationality: "",
    description: "",
  });
  const [roles, setRoles] = useState<RoleDto[]>([]);
  const [editEmailVerified, setEditEmailVerified] = useState<boolean>(true);

  const [userViewData, setUserViewData] = useState<UserDto>({
    id: "",
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    status: true,
    roles: [],
    height: "",
    dateOfBirth: "",
    position: "",
    city: "",
    state: "",
    country: "Australia",
    club: "",
    nationality: "",
    description: "",
  });
  const [isViewMode, setIsViewMode] = useState(false); // to distinguish between view and edit

  const dispatch = useDispatch();
  const navigate = useNavigate();
   const rolesInTable = Array.from(
    new Map(userList.records.flatMap((u) => u.roles).map((r) => [r.id, r])).values()
  );
const getUsers = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));
      
      const params: any = {
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        search: searchTerm.trim(),
        sort: "last_name", 
        sortDirection: "ASC",
      };

      if (selectedRole) {
        params.role = selectedRole;
      }

      const queryParams = new URLSearchParams(params).toString();
      const res = await GetApiMethod("user?" + queryParams);

      // Sort by createdAt on frontend in descending order (newest first)
      const sortedUsers = res.data.data.records.sort(
        (a: any, b: any) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA; // DESC order (newest first)
        }
      );

      setUserList({
        records: sortedUsers,
        totalCount: res.data.data.totalCount,
      });
      dispatch(showLoader({ showLoader: false }));
    } catch (err) {
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch, page, rowsPerPage, searchTerm, selectedRole]);



  const getRoles = useCallback(async () => {
    try {
      dispatch(showLoader({ showLoader: true }));
      const res = await GetApiMethod("roles?ismobile=false&page=1&limit=100&sort=displayName&sortDirection=ASC");
      setRoles(res.data.data.records);
      dispatch(showLoader({ showLoader: false }));
    } catch (error) {
      setRoles([]);
      dispatch(showLoader({ showLoader: false }));
    }
  }, [dispatch]);

  useEffect(() => {
    getRoles();
  }, [getRoles]);

  useEffect(() => {
    getUsers();
  }, [getUsers, page, rowsPerPage, searchTerm, selectedRole]);


  const resetForm = useCallback(() => {
    setFormModal(false);
    setUserData({
      id: "",
      email: "",
      firstName: "",
      lastName: "",
      phone: "",
      status: false,
      roles: [],
      height: "",
      dateOfBirth: "",
      position: "",
      city: "",
      state: "",
      country: "Australia",
      club: "",
      nationality: "",
      description: "",
    });

    }, []);

  const userSubmit = useCallback(
    async (values: UserDto) => {
      try {
        dispatch(showLoader({ showLoader: true }));

        const finalValues: any = {
          ...values,
          phoneNumber: values.phone,
          roles: values.roles.map((r) => r.id),
        };

        // If editing an unverified user, do not send status (backend blocks it)
        if (finalValues.id && editEmailVerified === false) {
          delete finalValues.status;
        }

        const apiMethod = finalValues.id
          ? PutApiMethod("user/" + finalValues.id, finalValues)
          : PostApiMethod("user", finalValues);
        await apiMethod;
        resetForm();
        dispatch(showLoader({ showLoader: false }));

        getUsers();
      } catch (error) {
        dispatch(showLoader({ showLoader: false }));
        const errorMsg = DisplayErrorMessages(error);

        dispatch(
          showToaster({
            dialogBgColor: "bg-danger",
            dialogMsg: errorMsg,
            showDialog: true,
            timer: "5000",
          })
        );
      }
    },
    [dispatch, getUsers, resetForm]
  );
  const deleteUser = useCallback(
  async (userId: string) => {
    try {
      if (!userId) return;

      dispatch(showLoader({ showLoader: true }));

      // Fetch current user details to satisfy update validator requirements
      const res = await GetApiMethod(`user/${userId}`);
      const data = res?.data?.data || {};

      const payload: any = {
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phone,
        status: data.status,
        isDelete: true,
      };

      await DeleteApiMethod(`user/${userId}`);

      dispatch(showLoader({ showLoader: false }));

      // Refresh user list
      getUsers();

      dispatch(
        showToaster({
          dialogBgColor: "bg-success",
          dialogMsg: "User deleted successfully",
          showDialog: true,
          timer: 3000,
        })
      );
    } catch (error) {
      dispatch(showLoader({ showLoader: false }));

      const errorMsg = DisplayErrorMessages(error);
      dispatch(
        showToaster({
          dialogBgColor: "bg-danger",
          dialogMsg: errorMsg,
          showDialog: true,
          timer: 5000,
        })
      );
    }
  },
  [dispatch, getUsers]
);


  return (
    <>
      <AuthorizedLayout>
        <BoxHeader
          dashboard_name="User Management"
          button_name={"Add User"}
          image={SelectedUserMenu}
          onAdd={() => {
            setFormModal(true);
            setUserData({
              id: "",
              email: "",
              firstName: "",
              lastName: "",
              phone: "",
              status: false,
              roles: [],
              height: "",
              dateOfBirth: "",
              position: "",
              city: "",
              state: "",
              country: "Australia",
              club: "",
              nationality: "",
              description: "",
            });
          }}
          button_show={PermissionCheck("manage_users")}
        >
          <AntTabs value={0}>
            <AntTab label="All Users" className="tab-menu" />
          </AntTabs>
        </BoxHeader>
        {/* <TableSearch
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder="Search Users by name, email, phone or role..."
      /> */}
        <Box display="flex" alignItems="center" gap={4} mt={2}>
          {/* Search Input */}
          <TableSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search Users by name, email, phone or role..."
          />

          {/* Role Filter with fixed label */}
          <Box sx={{ display: "flex", flexDirection: "column", minWidth: 200 }}>
            <Typography variant="caption" sx={{ mb: 0.5, color: "text.secondary", fontSize: 'inherit' }}>
              Filter By Role : 
            </Typography>
            <TextField
              select
              value={selectedRole || "null"} // use "null" for All Roles
              onChange={(e) => setSelectedRole(e.target.value === "null" ? "" : e.target.value)}
              variant="standard"
              size="small"
              sx={{ minWidth: 200 }}
              SelectProps={{
                renderValue: (selected) => {
                  const val = selected as string;
                  if (val === "null" || val === "") return "All Roles"; // show All Roles
                  const role = rolesInTable.find((r) => r.id === val);
                  return role ? role.displayName : "";
                },
              }}
            >
              <MenuItem value="null">All Roles</MenuItem>
              {rolesInTable.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.displayName}
                </MenuItem>
              ))}
            </TextField>

          </Box>
        </Box>
        <TableContainer className="mt-2">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Roles</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Detail</TableCell>

                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {userList.records.map((val, key) => (
                <TableRow key={key}>
                  <TableCell>{val.firstName + " " + val.lastName}</TableCell>
                  <TableCell>{val.email}</TableCell>
                  <TableCell>{val.phone}</TableCell>
                  <TableCell>{val.roles.map(role => role.displayName).join(", ")}</TableCell>

                  {/* <TableCell> */}
                    {/* <Switch
                      checked={val.status} // true = Active, false = InActive
                      onChange={(e) => {
                        const newStatus = e.target.checked;
                      }}
                      color="primary"
                      disabled={true}
                    /> */}
                  <TableCell>{val.status ? "Active" : "Inactive"}</TableCell>

                  {/* </TableCell> */}
                  <TableCell
                    sx={{
                      textDecoration: "underline",
                      color: "blue",
                      cursor: "pointer",
                    }}
                    onClick={() => navigate(`/user/${val.id}`)}
                  >
                    View
                  </TableCell>

                  <TableCell align="right">
                    <MenuGroup
                      id={val.id}
                      view={false}
                      edit={true}
                      delete={true}
                      clickEdit={async () => {
                        try {
                          setFormModal(true);
                          dispatch(showLoader({ showLoader: true }));
                          const res = await GetApiMethod(`user/${val.id}`);
                          const data = res.data?.data || {};

                          const fetchedRoles = Array.isArray(data.roles) ? data.roles : [];
                          const mappedRoles = fetchedRoles
                            .map((r: any) => roles.find((x) => x.id === r.id))
                            .filter(Boolean) as RoleDto[];
                          const finalRoles = mappedRoles.length > 0 ? mappedRoles : (val.roles as any);

                          setUserData({
                            id: data.id || val.id,
                            email: data.email || val.email,
                            firstName: data.firstName || val.firstName,
                            lastName: data.lastName || val.lastName,
                            phone: data.phone || val.phone,
                            status: typeof data.status === "boolean" ? data.status : val.status,
                            roles: finalRoles,
                            height: data.height || (val as any).height || "",
                            dateOfBirth: data.dateOfBirth || (val as any).dateOfBirth || (val as any).date_of_birth || "",
                            position: data.position || (val as any).position || "",
                            city: data.city || (val as any).city || "",
                            state: data.state || (val as any).state || "",
                            country: data.country || (val as any).country || "Australia",
                            club: data.club || (val as any).club || "",
                            nationality: data.nationality || (val as any).nationality || "",
                            description: data.description || (val as any).description || "",
                          });
                          setEditEmailVerified(Boolean(data.emailVerified));
                          dispatch(showLoader({ showLoader: false }));
                        } catch (e) {
                          dispatch(showLoader({ showLoader: false }));
                          setUserData({
                            id: val.id,
                            email: val.email,
                            firstName: val.firstName,
                            lastName: val.lastName,
                            phone: val.phone,
                            status: val.status,
                            roles: val.roles,
                            height: (val as any).height || "",
                            dateOfBirth: (val as any).dateOfBirth || (val as any).date_of_birth || "",
                            position: (val as any).position || "",
                            city: (val as any).city || "",
                            state: (val as any).state || "",
                            country: (val as any).country || "Australia",
                            club: (val as any).club || "",
                          });
                          setEditEmailVerified(true);
                        }
                      }}
                      clickDelete={() => {
                        if (val.id) deleteUser(val.id);
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
                {userList.records.length === 0 && (
                <TableRow>
                  <TableCell align="center">
                    No Data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination
          count={userList.totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />

        <CustomModal
          open={formModal}
          onClose={() => {
            resetForm();
            dispatch(showLoader({ showLoader: false }));
          }}
          header={`${userData?.id ? "Edit" : "Create"} User`}
          style={ModalStyle({ width: 600, height: "auto" })}
        >
          <Formik
            initialValues={userData}
            validationSchema={getUserSchema((userData.roles?.[0]?.displayName || (userData.roles?.[0] as any)?.roleName || ""))}
            onSubmit={async (values) => {
              userSubmit(values);
            }}
            enableReinitialize={true}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              errors,
              touched,
              setFieldValue,
            }) => (
              <>
                <Box className="modal-body">
                  <Form onSubmit={handleSubmit}>
                    <Grid item md={12} m={2}>
                      <Grid container spacing={1} className="p-3">
                        <FormLabel className="fw-bold">
                          First Name:<sup className="text-danger">*</sup>
                        </FormLabel>
                        <TextField
                          className="w-100"
                          placeholder="Type here..."
                          variant="standard"
                          value={values.firstName}
                          name="firstName"
                          onChange={handleChange}
                        />
                        {touched.firstName && errors.firstName && (
                          <div className="text-danger">{errors.firstName}</div>
                        )}
                      </Grid>

                      <Grid container spacing={1} className="p-3">
                        <FormLabel className="fw-bold">
                          Last Name:<sup className="text-danger">*</sup>
                        </FormLabel>
                        <TextField
                          className="w-100"
                          placeholder="Type here..."
                          variant="standard"
                          value={values.lastName}
                          name="lastName"
                          onChange={handleChange}
                        />
                        {touched.lastName && errors.lastName && (
                          <div className="text-danger">{errors.lastName}</div>
                        )}
                      </Grid>

                      <Grid container spacing={1} className="p-3">
                        <FormLabel className="fw-bold">
                          Email:<sup className="text-danger">*</sup>
                        </FormLabel>
                        <TextField
                          className="w-100"
                          placeholder="Type here..."
                          variant="standard"
                          value={values.email}
                          name="email"
                          onChange={handleChange}
                        />
                        {touched.email && errors.email && (
                          <div className="text-danger">{errors.email}</div>
                        )}
                      </Grid>

                      <Grid container spacing={1} className="p-3">
                        <FormLabel className="fw-bold">
                          Phone:<sup className="text-danger">*</sup>
                        </FormLabel>
                        <TextField
                          className="w-100"
                          placeholder="Type here..."
                          variant="standard"
                          value={values.phone}
                          name="phone"
                          onChange={handleChange}
                        />
                        {touched.phone && errors.phone && (
                          <div className="text-danger">{errors.phone}</div>
                        )}
                      </Grid>
                      {
                        userData?.id  && (

                          <Grid
                          container
                          spacing={1}
                          alignItems={"center"}
                          className="p-3"
                        >
                          <FormLabel
                            className="fw-bold"
                            style={{ marginRight: 8 }}
                          >
                            Status:
                          </FormLabel>
                          <Switch
                            name="status"
                            checked={values.status} // true = Active, false = InActive
                            onChange={handleChange}
                            color="primary"
                            disabled={values.id === "" ? true : false}
                          />
                        </Grid>
                        )
                      }
                      <Box mb={2} ml={1}>
                        <FormLabel className="fw-bold">
                          Role:<sup className="text-danger">*</sup>
                        </FormLabel>
                        <TextField
                          select
                          variant="standard"
                          fullWidth
                          value={values.roles[0]?.id || "null"} // default to "null"
                          onChange={(e) => {
                            if (e.target.value === "null") {
                              setFieldValue("roles", []); // reset
                            } else {
                              const selectedRole = roles.find((r) => r.id === e.target.value);
                              if (selectedRole) setFieldValue("roles", [selectedRole]);
                            }
                          }}
                          SelectProps={{
                            renderValue: (selected) => {
                              if (!selected || selected === "null") return "Select Role"; // show placeholder in input
                              const role = roles.find((r) => r.id === selected);
                              return role ? role.displayName : "Select Role";
                            },
                          }}
                        >
                          <MenuItem value="null">Select Role</MenuItem>
                          {roles.map((role) => (
                            <MenuItem key={role.id} value={role.id}>
                              {role.displayName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>

                      {/* Role-specific fields */}
                      {(() => {
                        const selectedRoleName = values.roles[0]?.displayName || (values.roles[0] as any)?.roleName || "";
                        const fields = getRoleSpecificFields(selectedRoleName);
                        if (fields.length === 0) return null;

                        return (
                          <>
                            {fields.includes("height") && (
                              <Grid container spacing={1} className="p-3">
                                <FormLabel className="fw-bold">Height (cm):</FormLabel>
                                <TextField
                                  className="w-100"
                                  placeholder="e.g. 180"
                                  variant="standard"
                                  value={values.height || ""}
                                  name="height"
                                  onChange={handleChange}
                                />
                              </Grid>
                            )}

                            {fields.includes("dateOfBirth") && (
                              <Grid container spacing={1} className="p-3">
                                <FormLabel className="fw-bold">Date of Birth:</FormLabel>
                                <TextField
                                  className="w-100"
                                  type="date"
                                  variant="standard"
                                  value={(values.dateOfBirth || "").toString().slice(0,10)}
                                  name="dateOfBirth"
                                  onChange={handleChange}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </Grid>
                            )}

                            {fields.includes("position") && (
                              <Grid container spacing={1} className="p-3">
                                <FormLabel className="fw-bold">Position:</FormLabel>
                                <TextField
                                  className="w-100"
                                  placeholder="Type here..."
                                  variant="standard"
                                  value={values.position || ""}
                                  name="position"
                                  onChange={handleChange}
                                />
                              </Grid>
                            )}

                            {fields.includes("club") && (
                              <Grid container spacing={1} className="p-3">
                                <FormLabel className="fw-bold">Club:</FormLabel>
                                <TextField
                                  className="w-100"
                                  placeholder="Type here..."
                                  variant="standard"
                                  value={values.club || ""}
                                  name="club"
                                  onChange={handleChange}
                                />
                              </Grid>
                            )}

                            {fields.includes("city") && (
                              <Grid container spacing={1} className="p-3">
                                <FormLabel className="fw-bold">City:</FormLabel>
                                <TextField
                                  className="w-100"
                                  placeholder="Type here..."
                                  variant="standard"
                                  value={values.city || ""}
                                  name="city"
                                  onChange={handleChange}
                                />
                              </Grid>
                            )}

                            {fields.includes("state") && (
                              <Grid container spacing={1} className="p-3">
                                <FormLabel className="fw-bold">State:</FormLabel>
                                <TextField
                                  className="w-100"
                                  placeholder="Type here..."
                                  variant="standard"
                                  value={values.state || ""}
                                  name="state"
                                  onChange={handleChange}
                                />
                              </Grid>
                            )}

                            {fields.includes("country") && (
                              <Grid container spacing={1} className="p-3">
                                <FormLabel className="fw-bold">Country:</FormLabel>
                                <TextField
                                  className="w-100"
                                  placeholder="Australia"
                                  variant="standard"
                                  value={values.country || "Australia"}
                                  name="country"
                                  onChange={handleChange}
                                />
                              </Grid>
                            )}
                          </>
                        );
                      })()}

                      {/* Nationality field - always show */}
                      <Grid container spacing={1} className="p-3">
                        <FormLabel className="fw-bold">Nationality:</FormLabel>
                        <TextField
                          className="w-100"
                          placeholder="Type here..."
                          variant="standard"
                          value={values.nationality || ""}
                          name="nationality"
                          onChange={handleChange}
                        />
                      </Grid>

                      {/* Description field - always show */}
                      <Grid container spacing={1} className="p-3">
                        <FormLabel className="fw-bold">Description:</FormLabel>
                        <TextField
                          className="w-100"
                          placeholder="Type here..."
                          variant="standard"
                          multiline
                          rows={3}
                          value={values.description || ""}
                          name="description"
                          onChange={handleChange}
                        />
                      </Grid>

                    </Grid>
                    

                      <Box className="d-flex justify-content-end mt-3 p-3">
                      <button className="btn btn-danger" type="submit">
                        Submit
                      </button>
                    </Box>
                  </Form>
                </Box>
              </>
            )}
          </Formik>
        </CustomModal>

        <CustomModal
          open={isViewMode}
          onClose={() => {
            setIsViewMode(false);
          }}
          header={`View User`}
          style={ModalStyle({ width: 600, height: "auto" })}
        >
          <Box className="modal-body">
            <Grid item md={12} m={2}>
              <Grid container spacing={1} className="p-3">
                <FormLabel className="fw-bold">First Name:</FormLabel>
                <Typography variant="body1" className="ms-2">
                  {userViewData?.firstName || "-"}
                </Typography>
              </Grid>

              <Grid container spacing={1} className="p-3">
                <FormLabel className="fw-bold">Last Name:</FormLabel>
                <Typography variant="body1" className="ms-2">
                  {userViewData?.lastName || "-"}
                </Typography>
              </Grid>

              <Grid container spacing={1} className="p-3">
                <FormLabel className="fw-bold">Email:</FormLabel>
                <Typography variant="body1" className="ms-2">
                  {userViewData?.email || "-"}
                </Typography>
              </Grid>

              <Grid container spacing={1} className="p-3">
                <FormLabel className="fw-bold">Phone:</FormLabel>
                <Typography variant="body1" className="ms-2">
                  {userViewData?.phone || "-"}
                </Typography>
              </Grid>

              <Grid container spacing={1} alignItems={"center"} className="p-3">
                <FormLabel className="fw-bold" style={{ marginRight: 8 }}>
                  Status:
                </FormLabel>
                <Switch
                  checked={userViewData?.status || false}
                  color="primary"
                  disabled
                />
                <Typography variant="body2" className="ms-2">
                  {userViewData?.status ? "Active" : "Inactive"}
                </Typography>
              </Grid>

              <Box mt={2} ml={1}>
                <FormLabel className="fw-bold">Roles:</FormLabel>
                <Box
                  display="flex"
                  flexDirection="column"
                  ml={2}
                  mt={1}
                  sx={{
                    maxHeight: 300,
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    msOverflowStyle: "none",
                  }}
                >
                  {roles.map((item, index) => {
                    const hasRole = userViewData?.roles?.some(
                      (r) => r.id === item.id
                    );
                    return (
                      <FormControlLabel
                        key={index}
                        control={<Checkbox checked={hasRole} disabled />}
                        label={item.displayName}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Grid>
          </Box>
        </CustomModal>
      </AuthorizedLayout>
    </>
  );
};

export default UserManagement;
